import React, { useMemo } from 'react'

import { useAppDataContext } from '@cox/core-ui8/dist/AppDataContext'
import { useFlexOffers } from '@cox/core-ui8/dist/useFlexOffers'

import { useInversionData } from '../../../hooks/useInversionData'
import { getInversionTokenValues } from '../../../utils/inversion-util'
import { getImageSrc } from '../../../utils/local-util'
import { getAttributeValue, hasCFConditionals } from '../../../utils/rule-builder-util'
import ReactImage from '../../ReactImage'
import ReactText from '../../ReactText'
import { ReactListProps } from '../types'
import './ReactCFConditionalList.scss'

const ReactCFConditionalList = (props: ReactListProps) => {
  const { ruleExpression = '', index, tokenProperties, elements = {}, maxLength } = props

  const flexOffers = useFlexOffers(ruleExpression)

  const getOptionsAttributeValue = (attribute: any, propertyName?: string) => {
    const bulletAttributeArray = []

    // Get the value for each bullet if conditions are met:
    const getAttribute = (propertyName: string, baseProperty: string) => {
      const refinedAttribute = { ...attribute }

      function checkBulletNumber(bullet: any) {
        if (propertyName == 'bulletPoints') {
          return bullet?.points == baseProperty
        } else if (propertyName == 'bulletIcon') {
          return bullet?.bulletIcons == baseProperty
        } else if (propertyName == 'bulletIconAltText') {
          return bullet?.iconsAltText == baseProperty
        }
      }

      let refinedBullets = refinedAttribute?.value
      refinedBullets = refinedBullets && JSON.parse(refinedBullets).filter(checkBulletNumber)

      if (refinedBullets?.length > 0) {
        refinedAttribute.value = refinedBullets && JSON.stringify(refinedBullets)
        const attributeValue = getAttributeValue(refinedAttribute, flexOffers, index, tokenProperties, baseProperty)
        return attributeValue
      }
    }
    const maximumBulletLength = maxLength || (attribute?.value && JSON.parse(attribute.value).length)
    for (let i = 1; i <= maximumBulletLength; i++) {
      // Get base property name:
      let baseProperty = ''
      if (propertyName == 'bulletPoints') {
        baseProperty = `bulletText${i}`
      } else if (propertyName == 'bulletIcon') {
        baseProperty = `bulletIcons${i}`
      } else if (propertyName == 'bulletIconAltText') {
        baseProperty = `bulletIconAltText${i}`
      }

      // Get attribute for the bullet:

      const attribute = propertyName && getAttribute(propertyName, baseProperty)

      // If attribute meets conditions and exists, push into the attribute array:
      if (attribute) {
        bulletAttributeArray.push(attribute)
      }
    }

    return bulletAttributeArray
  }

  const inversionItems = useInversionData()
  const { appData } = useAppDataContext()
  const isLocal = appData?.page?.isLocal
  const invTokenregex = /\[\[(?!token|offer)[^\]]+\]\]/g
  const newItems = useMemo(() => inversionItems, [inversionItems])

  const getValue = (attribute: any, propertyName?: string) => {
    if (isLocal) {
      // Get string attribute:
      let stringAttribute
      if (typeof attribute == 'string') {
        stringAttribute = attribute
      } else if (typeof attribute == 'object' && typeof attribute.value == 'string') {
        stringAttribute = attribute.value
      }

      // Get token replaced string:
      if (stringAttribute && invTokenregex.test(stringAttribute)) {
        const matches = stringAttribute.match(invTokenregex)
        let tokenReplacedString: any = stringAttribute
        tokenReplacedString = matches?.reduce((acc: string, invToken: any) => {
          return acc.replace(invToken, () => {
            return getInversionTokenValues(invToken, newItems) ?? ''
          })
        }, stringAttribute)
        attribute = tokenReplacedString
      }
    }

    const hasJsonAttribute = hasCFConditionals(attribute)
    if (hasJsonAttribute) {
      const jsonAttribute = (elements as any)?.[`${propertyName}_json`]
      if (propertyName?.includes('bullet')) {
        return getOptionsAttributeValue(jsonAttribute, propertyName)
      } else return getAttributeValue(jsonAttribute, flexOffers, index, tokenProperties, propertyName, hasJsonAttribute)
    }
    return getAttributeValue(attribute, flexOffers, index, tokenProperties, propertyName)
  }

  const { bulletPoints = { value: '' }, bulletIcon = { value: '' }, bulletIconAltText = { value: '' } } = elements
  const bulletPointsValue = getValue(bulletPoints, 'bulletPoints')
  const bulletIconsValue = getValue(bulletIcon, 'bulletIcon')
  const bulletIconAltTextsValue = getValue(bulletIconAltText, 'bulletIconAltText')
  const bulletList = {
    iconsArray: bulletIconsValue,
    altIconsArray: bulletIconAltTextsValue,
    itemsArray: bulletPointsValue
  }
  const { iconsArray, altIconsArray, itemsArray } = bulletList

  return (
    <div className='react-custom-list-container'>
      {itemsArray?.length > 0 && (
        <div className='bullet-list' data-testid={`react-custom-list-container`}>
          {itemsArray?.map((item: any, index: number) => {
            const icon = iconsArray && getImageSrc(iconsArray[index])
            const altIcon = altIconsArray && altIconsArray[index]
            const hasBulletIcon = icon || altIcon

            return (
              item && (
                <div className='bullet-list-item' key={`${item}${index}`} data-testid={`react-cf-list`}>
                  {hasBulletIcon && (
                    <ReactImage
                      src={icon}
                      imgCss={`bullet-icon ${icon && 'background-none'}`}
                      ariaLabel='bullet-icon'
                      alt={altIcon}
                      asIcon
                    ></ReactImage>
                  )}
                  <ReactText className='bullet-text' text={item} isParsed />
                </div>
              )
            )
          })}
        </div>
      )}
    </div>
  )
}
export default ReactCFConditionalList
